import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { UserRole } from '../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: UserRole[];
  requireVerifiedEmail?: boolean;
  requireBranches?: boolean;
}

export default function ProtectedRoute({
  children,
  roles,
  requireVerifiedEmail = false,
  requireBranches = false,
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 text-stone-500">
        <div className="flex items-center gap-2">
          <i className="ri-loader-4-line text-2xl animate-spin" />
          <span className="text-sm">載入中...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    const isAdminPath = location.pathname.startsWith('/admin');
    const loginPath = isAdminPath ? '/admin/login' : '/';
    return <Navigate to={loginPath} state={{ from: location.pathname }} replace />;
  }

  if (!user.isActive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <p className="text-sm text-stone-600">帳號已停用，請聯絡管理員。</p>
      </div>
    );
  }

  if (roles && !roles.includes(user.role)) {
    if (user.role === 'staff' || user.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (requireVerifiedEmail && !user.emailVerified) {
    return <Navigate to="/account/verify-email" replace />;
  }

  if (requireBranches && user.role === 'staff' && user.branchIds.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-bold text-stone-800 mb-2">尚未指派分店</h1>
          <p className="text-sm text-stone-500">您的帳號尚未被指派可管理的分店，請聯絡系統管理員。</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
